/**
 * Settings → Environments (docs/environments.md): the tenant's environments in the order of the switcher, with
 * their colour, name, protection, default, and what they hold. Owners change them in place; everyone else reads.
 * The activity (audit) log of environment, vault and key changes sits under the list, for owners.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useAuth } from '../lib/auth.tsx';
import { Api, ApiError, type EnvironmentChange } from '../lib/api.ts';
import { useEnv } from '../lib/envs.tsx';
import { ENV_COLORS, auditPhrase, envColor, type AuditEntry, type Environment } from '../lib/env.ts';
import { ago, dateTime } from '../lib/format.ts';
import { IconButton, Menu, Surface, cx } from '../design/primitives.tsx';
import { ChevronIcon, PlusIcon, TrashIcon } from '../components/icons.tsx';
import { useFormDraft, useUrlFlag } from '../lib/formstate.ts';
import { clearForm } from '../lib/pagedraft.ts';
import { Button, Field, Spinner, inputClass, useToast } from '../components/ui.tsx';
import { Confirm, Panel, Title } from './Settings.tsx';

const message = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

export function EnvironmentsSettings() {
  const { tenant, canManage } = useAuth();
  const { envs, reload } = useEnv();
  const toast = useToast();
  const tid = tenant?.id ?? '';
  const [creating, setCreating] = useUrlFlag('new');
  const [deleting, setDeleting] = useState<Environment>();
  const [logKey, setLogKey] = useState(0);
  const sorted = [...envs].sort((a, b) => a.position - b.position);

  const change = async (e: Environment, c: EnvironmentChange, done?: string): Promise<boolean> => {
    try {
      await Api.updateEnvironment(tid, e.id, c);
      if (done) toast(done);
      reload();
      setLogKey((k) => k + 1);
      return true;
    } catch (err) {
      toast(message(err), 'fail');
      return false;
    }
  };

  return (
    <>
      <Title action={canManage && <Button variant="primary" aria-expanded={creating} onClick={() => { if (creating) clearForm(`environment.${tid}`); setCreating(!creating); }}><PlusIcon size={16} />New environment</Button>}>Environments</Title>
      <p className="m-0 -mt-2 mb-6 text-small text-n8 max-w-3xl">
        Vaults, connector keys and everything you look at belong to one environment. The default is used when nothing names one; a protected environment is always marked in the app.
      </p>
      <div className="flex flex-col gap-4">
        {creating && <NewEnvironment tid={tid} onClose={() => setCreating(false)} onCreated={() => { reload(); setLogKey((k) => k + 1); }} />}
        {deleting && (
          <Confirm question={`Delete environment ${deleting.name}?`} action="Delete" onCancel={() => setDeleting(undefined)} onConfirm={async () => {
            try {
              await Api.deleteEnvironment(tid, deleting.id);
              toast(`Environment ${deleting.name} deleted`);
              reload();
              setLogKey((k) => k + 1);
            } catch (err) {
              toast(message(err), 'fail');
            }
            setDeleting(undefined);
          }}>
            The environment is empty. Its name and colour are gone; nothing else is affected.
          </Confirm>
        )}
        <Surface className="px-4 py-1">
          {sorted.map((e, i) => (
            <Row key={e.id} env={e} first={i === 0} last={i === sorted.length - 1} index={i} canManage={canManage}
              onChange={(c, done) => change(e, c, done)} onDelete={() => setDeleting(e)} />
          ))}
        </Surface>
      </div>
      {canManage && tid && <ActivityLog tid={tid} refreshKey={logKey} />}
    </>
  );
}

function Row({ env: e, first, last, index, canManage, onChange, onDelete }: {
  env: Environment; first: boolean; last: boolean; index: number; canManage: boolean;
  onChange: (c: EnvironmentChange, done?: string) => Promise<boolean>; onDelete: () => void;
}) {
  const [name, setName] = useState(e.name);
  const editing = useRef(false);
  // The list reloads behind us: follow it unless the name is being typed
  useEffect(() => { if (!editing.current) setName(e.name); }, [e.name]);
  const commit = async () => {
    editing.current = false;
    const next = name.trim();
    if (!next || next === e.name) return setName(e.name);
    if (!(await onChange({ name: next }, 'Renamed'))) setName(e.name);
  };
  const deletable = canManage && !e.isDefault && e.vaultCount === 0;
  const why = e.isDefault ? 'The default environment cannot be deleted' : e.vaultCount > 0 ? 'Move or revoke its vaults first' : `Delete ${e.name}`;

  return (
    <div className={cx('py-3 flex items-center gap-4 flex-wrap', !first && 'border-t border-n4')}>
      <Menu.Root>
        <Menu.Trigger disabled={!canManage} aria-label={`Colour of ${e.name}: ${e.color}`}
          className="size-8 rounded-full grid place-items-center bg-transparent border-0 p-0 cursor-pointer disabled:cursor-default outline-none enabled:hover:bg-n3 focus-visible:ring-2 focus-visible:ring-accent">
          <i className="size-4 rounded-full" style={{ background: envColor(e.color) }} />
        </Menu.Trigger>
        <Menu.Content align="start">
          <Menu.Label>Colour</Menu.Label>
          {ENV_COLORS.map((c) => (
            <Menu.Item key={c} selected={c === e.color} onSelect={() => void onChange({ color: c })}>
              <span className="flex items-center gap-2.5"><i className="size-3 rounded-full" style={{ background: envColor(c) }} />{c}</span>
            </Menu.Item>
          ))}
        </Menu.Content>
      </Menu.Root>

      <div className="flex-1 min-w-[180px] flex flex-col gap-0.5">
        {canManage ? (
          <input aria-label={`Name of ${e.name}`} value={name} maxLength={60} onChange={(ev) => { editing.current = true; setName(ev.target.value); }}
            onBlur={() => void commit()}
            onKeyDown={(ev) => { if (ev.key === 'Enter') ev.currentTarget.blur(); if (ev.key === 'Escape') { editing.current = false; setName(e.name); ev.currentTarget.blur(); } }}
            className="h-8 -ml-2 px-2 rounded-chip bg-transparent text-body font-medium text-n10 border-0 outline-none hover:bg-n3 focus:bg-n3 focus-visible:ring-[1.5px] focus-visible:ring-accent w-full max-w-xs" />
        ) : <span className="text-body font-medium">{e.name}</span>}
        <span className="text-micro text-n7 tabular-nums">
          {e.vaultCount} {e.vaultCount === 1 ? 'vault' : 'vaults'} ·{' '}
          <span className="inline-flex items-center gap-1.5"><i className={cx('size-1.5 rounded-full', e.onlineCount ? 'bg-ok' : 'bg-n6')} aria-hidden="true" />{e.onlineCount} online</span>
        </span>
      </div>

      <label className={cx('inline-flex items-center gap-2 text-small text-n9', canManage ? 'cursor-pointer' : 'cursor-default')}>
        <button type="button" role="switch" aria-checked={e.protected} disabled={!canManage} onClick={() => void onChange({ protected: !e.protected })}
          className={cx('relative w-8 h-[18px] rounded-full border-0 p-0 transition-colors duration-[var(--t-micro)] disabled:opacity-60 outline-none focus-visible:ring-2 focus-visible:ring-accent',
            canManage && 'cursor-pointer', e.protected ? 'bg-accent' : 'bg-n5')}>
          <span className={cx('absolute top-0.5 left-0.5 size-3.5 rounded-full bg-n2 transition-transform duration-[var(--t-micro)]', e.protected && 'translate-x-3.5')} />
        </button>
        Protected
      </label>

      <label className={cx('inline-flex items-center gap-2 text-small text-n9', canManage && !e.isDefault ? 'cursor-pointer' : 'cursor-default')}>
        <input type="radio" name="default-environment" checked={e.isDefault} disabled={!canManage || e.isDefault}
          onChange={() => void onChange({ isDefault: true }, `${e.name} is now the default`)} className="accent-[var(--accent)] size-4" />
        Default
      </label>

      {canManage && (
        <div className="flex items-center gap-0.5">
          <IconButton label={`Move ${e.name} up`} disabled={first} className="disabled:opacity-30 disabled:pointer-events-none" onClick={() => void onChange({ position: index - 1 })}>
            <ChevronIcon size={16} className="rotate-180" />
          </IconButton>
          <IconButton label={`Move ${e.name} down`} disabled={last} className="disabled:opacity-30 disabled:pointer-events-none" onClick={() => void onChange({ position: index + 1 })}>
            <ChevronIcon size={16} />
          </IconButton>
          <IconButton label={`Delete ${e.name}`} title={why} disabled={!deletable} className="disabled:opacity-30 disabled:pointer-events-none" onClick={onDelete}>
            <TrashIcon size={16} />
          </IconButton>
        </div>
      )}
    </div>
  );
}

function NewEnvironment({ tid, onClose, onCreated }: { tid: string; onClose: () => void; onCreated: () => void }) {
  const toast = useToast();
  // Typed values are a draft in sessionStorage (Back returns to the filled form); a create or a cancel clears it
  const [{ name, color, prot }, setDraft, clearDraft] = useFormDraft(`environment.${tid}`, { name: '', color: 'blue', prot: false });
  const setName = (v: string) => setDraft({ name: v });
  const setColor = (v: string) => setDraft({ color: v });
  const setProt = (v: boolean) => setDraft({ prot: v });
  const close = () => { clearDraft(); onClose(); };
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await Api.createEnvironment(tid, { name: name.trim(), color, protected: prot });
      toast(`Environment ${name.trim()} created`);
      onCreated();
      close();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title="New environment" onClose={close}>
      <form onSubmit={(ev) => void submit(ev)} className="flex flex-col gap-4">
        <div className="flex items-end gap-4 flex-wrap">
          <Field label="Name" className="w-full max-w-xs" error={error}>
            <input className={inputClass} value={name} autoFocus maxLength={60} placeholder="QA" onChange={(ev) => { setName(ev.target.value); setError(undefined); }} />
          </Field>
          <fieldset className="border-0 p-0 m-0 flex flex-col gap-1">
            <legend className="text-small text-n8 p-0 mb-1">Colour</legend>
            <div className="flex gap-1.5 h-9 items-center" role="radiogroup" aria-label="Colour">
              {ENV_COLORS.map((c) => (
                <button key={c} type="button" role="radio" aria-checked={c === color} aria-label={c} onClick={() => setColor(c)}
                  className={cx('size-6 rounded-full grid place-items-center border-0 p-0 cursor-pointer bg-transparent outline-none focus-visible:ring-2 focus-visible:ring-accent', c === color && 'ring-[1.5px] ring-n10')}>
                  <i className="size-3.5 rounded-full" style={{ background: envColor(c) }} />
                </button>
              ))}
            </div>
          </fieldset>
          <label className="inline-flex items-center gap-2 text-small text-n9 h-9 cursor-pointer">
            <input type="checkbox" checked={prot} onChange={(ev) => setProt(ev.target.checked)} className="accent-[var(--accent)] size-4" />Protected
          </label>
        </div>
        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={busy || !name.trim()}>Create environment</Button>
          <Button variant="ghost" onClick={close}>Cancel</Button>
        </div>
      </form>
    </Panel>
  );
}

// ---------------------------------------------------------------- activity

const PAGE = 30;

function ActivityLog({ tid, refreshKey }: { tid: string; refreshKey: number }) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);

  const load = async (before?: number) => {
    setLoading(true);
    try {
      const page = await Api.audit(tid, PAGE, before);
      setEntries((prev) => (before ? [...prev, ...page] : page));
      setMore(page.length === PAGE);
      setError(undefined);
    } catch (err) {
      setError(message(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, [tid, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="flex flex-col gap-3 mt-12">
      <h2 className="m-0 text-body font-semibold">Activity</h2>
      {error && <p className="m-0 text-small text-fail">{error}</p>}
      {loading && entries.length === 0 && <Spinner />}
      {!loading && !error && entries.length === 0 && <Surface className="p-8 text-center text-small text-n8">Nothing has changed yet.</Surface>}
      {entries.length > 0 && (
        <Surface className="px-4 py-1">
          {entries.map((e, i) => {
            const p = auditPhrase(e);
            return (
              <div key={e.id} className={cx('py-2.5 flex items-baseline gap-4 text-small', i > 0 && 'border-t border-n4')}>
                <time dateTime={new Date(e.at).toISOString()} title={dateTime(e.at)} className="w-24 shrink-0 text-micro text-n7 whitespace-nowrap">{ago(e.at)}</time>
                <span className="min-w-0 text-n9 [overflow-wrap:anywhere]">
                  <b className="font-medium text-n10">{p.who}</b> {p.text}
                  {p.target && <> <b className="font-medium text-n10">{p.target}</b></>}
                </span>
              </div>
            );
          })}
        </Surface>
      )}
      {more && <div><Button variant="ghost" disabled={loading} onClick={() => void load(entries[entries.length - 1]?.id)}>Load more</Button></div>}
    </section>
  );
}
